import { useEffect, type ReactNode } from 'react';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { TOKEN_CONFIG } from '@beat-games/game-config';
import { useAuthStore } from '../../stores/authStore';
import { useCreditsStore } from '../../stores/creditsStore';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface BalanceGateProps {
  children: ReactNode;
}

export default function BalanceGate({ children }: BalanceGateProps) {
  const { connected, publicKey } = useWallet();
  const { connection } = useConnection();
  const { setVisible } = useWalletModal();

  const {
    balance,
    isEligible,
    isCheckingBalance,
    setWalletAddress,
    setIsConnected,
    setBalance,
    setIsEligible,
    setIsCheckingBalance,
  } = useAuthStore();

  const { fetchCredits } = useCreditsStore();

  useEffect(() => {
    setIsConnected(connected);
    setWalletAddress(publicKey?.toBase58() ?? null);

    if (!connected || !publicKey) {
      setBalance(null);
      setIsEligible(false);
      return;
    }

    async function checkBalance() {
      setIsCheckingBalance(true);
      try {
        const response = await fetch(
          `${API_BASE}/api/auth/balance?wallet=${publicKey!.toBase58()}`
        );
        const data = await response.json();
        if (data.success) {
          setBalance(data.data.balance);
          setIsEligible(data.data.balance >= TOKEN_CONFIG.minBalance);
        }

        // Also fetch credits
        await fetchCredits(publicKey!.toBase58());
      } catch (error) {
        console.error('Failed to check balance:', error);
        setBalance(0);
        setIsEligible(false);
      } finally {
        setIsCheckingBalance(false);
      }
    }

    checkBalance();
  }, [
    connected,
    publicKey,
    connection,
    setWalletAddress,
    setIsConnected,
    setBalance,
    setIsEligible,
    setIsCheckingBalance,
    fetchCredits,
  ]);

  // Not connected
  if (!connected) {
    return (
      <div className="relative bg-[#0a0a0a] rounded-2xl p-10 max-w-md mx-auto text-center border border-white/[0.06] overflow-hidden">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent pointer-events-none" />

        {/* Decorative corners */}
        <div className="absolute top-0 left-0 w-8 h-8 border-t border-l border-white/10 rounded-tl-2xl" />
        <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-white/10 rounded-tr-2xl" />
        <div className="absolute bottom-0 left-0 w-8 h-8 border-b border-l border-white/10 rounded-bl-2xl" />
        <div className="absolute bottom-0 right-0 w-8 h-8 border-b border-r border-white/10 rounded-br-2xl" />

        {/* Wallet icon */}
        <div className="relative w-16 h-16 mx-auto mb-6 rounded-full bg-white/[0.03] border border-white/[0.08] flex items-center justify-center">
          <svg className="w-7 h-7 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a2.25 2.25 0 00-2.25-2.25H15a3 3 0 11-6 0H5.25A2.25 2.25 0 003 12m18 0v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 9m18 0V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v3" />
          </svg>
        </div>

        <h2 className="relative font-display font-bold text-3xl mb-3 tracking-wide">CONNECT WALLET</h2>
        <p className="relative text-white/40 mb-8 text-sm">
          Connect your Solana wallet to start playing
        </p>
        <button
          onClick={() => setVisible(true)}
          className="relative group flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-white/[0.03] border border-white/[0.08] hover:border-[#00ff88]/30 hover:bg-[#00ff88]/5 transition-all duration-300"
        >
          <span className="text-sm font-semibold text-white/80 group-hover:text-[#00ff88] transition-colors">
            Select Wallet
          </span>
          <svg className="w-4 h-4 text-white/40 group-hover:text-[#00ff88] group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    );
  }

  // Checking balance
  if (isCheckingBalance) {
    return (
      <div className="relative bg-[#0a0a0a] rounded-2xl p-10 max-w-md mx-auto text-center border border-white/[0.06] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent pointer-events-none" />

        {/* Loading spinner */}
        <div className="relative w-12 h-12 mx-auto mb-6">
          <div className="absolute inset-0 rounded-full border-2 border-white/[0.06]" />
          <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#00ff88] animate-spin" />
        </div>

        <p className="relative text-white/40 text-sm">Checking token balance...</p>
      </div>
    );
  }

  // Insufficient balance
  if (!isEligible) {
    return (
      <div className="relative bg-[#0a0a0a] rounded-2xl p-10 max-w-md mx-auto text-center border border-white/[0.06] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#f59e0b]/[0.02] to-transparent pointer-events-none" />

        {/* Warning icon */}
        <div className="relative w-16 h-16 mx-auto mb-6 rounded-full bg-[#f59e0b]/10 border border-[#f59e0b]/20 flex items-center justify-center">
          <svg className="w-7 h-7 text-[#f59e0b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>

        <h2 className="relative font-display font-bold text-2xl mb-3 tracking-wide text-[#f59e0b]">
          INSUFFICIENT BALANCE
        </h2>
        <p className="relative text-white/40 mb-2 text-sm">
          You need at least{' '}
          <span className="text-white font-semibold">
            {TOKEN_CONFIG.minBalance.toLocaleString()} $BG
          </span>{' '}
          tokens to play.
        </p>
        <p className="relative text-white/40 mb-8 text-sm">
          Current balance:{' '}
          <span className="font-mono text-[#f59e0b]">
            {balance?.toLocaleString() ?? 0} $BG
          </span>
        </p>
        <a
          href="https://pump.fun/coin/B18zdobbWE7PXY2uKr9iB7HVho4nN5eSPzBHJtgDpump"
          target="_blank"
          rel="noopener noreferrer"
          className="relative group flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-[#f59e0b]/10 border border-[#f59e0b]/20 hover:border-[#f59e0b]/40 hover:bg-[#f59e0b]/15 transition-all duration-300"
        >
          <span className="text-sm font-semibold text-[#f59e0b]">
            Get $BG Tokens
          </span>
          <svg className="w-4 h-4 text-[#f59e0b] group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </div>
    );
  }

  // Eligible - render game
  return <>{children}</>;
}
